import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { SortOrder } from '@/types/common'
import { DEFAULT_PAGE } from '@/utils/constants'

import {
  DEFAULT_SORT_COLUMN,
  DEFAULT_SORT_DIRECTION,
  ERoleType,
  INITIAL_ADD_USERS_FORM_DATA,
} from './constants'
import {
  IAddUsersFormData,
  UserManagementFilters,
  UserManagementUser,
} from './types'
import { checkDuplicateEmailsInBatch } from './utils'

const DEVTOOLS_NAME = 'UserManagementStore'

/**
 * User Management Store State
 */
interface UserManagementState {
  searchText: string
  filters: UserManagementFilters
  page: number
  sort: {
    column: string | null
    direction: SortOrder
  }
  editingUser: UserManagementUser | null
  editDialogOpen: boolean
  deletingUser: UserManagementUser | null
  deleteDialogOpen: boolean
  selectedUser: UserManagementUser | null
  drawerOpen: boolean

  /** Current user's role (for permission checking) */
  currentUserRole: ERoleType | null

  /** Whether the invite users modal/dialog is open */
  isInviteModalOpen: boolean

  /** The role selected for the add users step */
  addUsersFormData: IAddUsersFormData

  /** Duplicate emails in batch */
  duplicateEmailsInBatch: Map<string, boolean>

  /** API errors per user row index after sending invitations (for inline display) */
  invitationErrorsByIndex: Record<number, string | undefined>

  /** Whether an email is being checked for duplication */
  isCheckingEmail: boolean
}

interface UserManagementStore extends UserManagementState {
  setSearchText: (searchText: string) => void
  setFilter: <K extends keyof UserManagementFilters>(
    key: K,
    value: UserManagementFilters[K]
  ) => void
  setPage: (page: number) => void
  setSort: (sort: { column: string | null; direction: SortOrder }) => void
  setEditingUser: (user: UserManagementUser | null) => void
  clearEditingUser: () => void
  setEditDialogOpen: (open: boolean) => void
  setDeletingUser: (user: UserManagementUser | null) => void
  clearDeletingUser: () => void
  setDeleteDialogOpen: (open: boolean) => void
  setSelectedUser: (user: UserManagementUser | null) => void
  setDrawerOpen: (open: boolean) => void
  resetFilters: () => void

  /** Set the current user's role */
  setCurrentUserRole: (role: ERoleType | null) => void

  /** Set the form data for the add users step */
  setAddUsersFormData: (formData: Partial<IAddUsersFormData> | null) => void

  /** Open the invite users modal */
  openInviteModal: () => void

  /** Close the invite users modal */
  closeInviteModal: () => void

  /** Set invitation API errors by row index (after Promise.allSettled) */
  setInvitationErrors: (
    errorsByIndex: Record<number, string | undefined>
  ) => void

  /** Set whether an email is being checked for duplication */
  setIsCheckingEmail: (isCheckingEmail: boolean) => void

  /** Clear invitation errors (e.g. when opening/closing modal) */
  clearInvitationErrors: () => void

  reset: () => void
}

const initialState = {
  searchText: '',
  filters: {} as UserManagementFilters,
  page: DEFAULT_PAGE,
  sort: {
    column: DEFAULT_SORT_COLUMN,
    direction: DEFAULT_SORT_DIRECTION as SortOrder,
  },
  editingUser: null as UserManagementUser | null,
  editDialogOpen: false,
  deletingUser: null as UserManagementUser | null,
  deleteDialogOpen: false,
  selectedUser: null as UserManagementUser | null,
  drawerOpen: false,

  currentUserRole: null,
  isInviteModalOpen: false,
  duplicateEmailsInBatch: new Map(),
  addUsersFormData: INITIAL_ADD_USERS_FORM_DATA,
  invitationErrorsByIndex: {},
  isCheckingEmail: false,
}

export const useUserManagementStore = create<UserManagementStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setSearchText: (searchText: string) => {
        set({ searchText, page: DEFAULT_PAGE }, false, 'setSearchText')
      },

      setFilter: <K extends keyof UserManagementFilters>(
        key: K,
        value: UserManagementFilters[K]
      ) => {
        set(
          (state) => ({
            filters: { ...state.filters, [key]: value },
            page: DEFAULT_PAGE,
          }),
          false,
          'setFilter'
        )
      },

      setPage: (page: number) => {
        set({ page }, false, 'setPage')
      },

      setSort: (sort) => {
        set({ sort }, false, 'setSort')
      },

      setEditingUser: (user: UserManagementUser | null) => {
        set(
          { editingUser: user, editDialogOpen: true },
          false,
          'setEditingUser'
        )
      },

      clearEditingUser: () => {
        set(
          { editingUser: null, editDialogOpen: false },
          false,
          'clearEditingUser'
        )
      },

      setEditDialogOpen: (open: boolean) => {
        set({ editDialogOpen: open }, false, 'setEditDialogOpen')
      },

      setDeletingUser: (user: UserManagementUser | null) => {
        set(
          { deletingUser: user, deleteDialogOpen: true },
          false,
          'setDeletingUser'
        )
      },

      clearDeletingUser: () => {
        set(
          { deletingUser: null, deleteDialogOpen: false },
          false,
          'clearDeletingUser'
        )
      },

      setDeleteDialogOpen: (open: boolean) => {
        set({ deleteDialogOpen: open }, false, 'setDeleteDialogOpen')
      },

      setSelectedUser: (user: UserManagementUser | null) => {
        set(
          { selectedUser: user, drawerOpen: !!user },
          false,
          'setSelectedUser'
        )
      },

      setDrawerOpen: (open: boolean) => {
        set(
          { drawerOpen: open, selectedUser: open ? undefined : null },
          false,
          'setDrawerOpen'
        )
      },

      resetFilters: () => {
        set({ filters: initialState.filters }, false, 'resetFilters')
      },

      setCurrentUserRole: (role) =>
        set({ currentUserRole: role }, false, 'userManagement/setRole'),

      setAddUsersFormData: (formData) => {
        const currentFormData = get().addUsersFormData

        const addUsersFormData = formData
          ? { ...currentFormData, ...formData }
          : INITIAL_ADD_USERS_FORM_DATA

        const { duplicates } = checkDuplicateEmailsInBatch(
          addUsersFormData.users
        )

        return set(
          {
            addUsersFormData,
            duplicateEmailsInBatch: duplicates,
            // invitationErrorsByIndex: {},
          },
          false,
          'userManagement/setAddUsersFormData'
        )
      },

      openInviteModal: () =>
        set(
          { isInviteModalOpen: true, invitationErrorsByIndex: {} },
          false,
          'userManagement/openInviteModal'
        ),

      closeInviteModal: () =>
        set(
          { isInviteModalOpen: false, invitationErrorsByIndex: {} },
          false,
          'userManagement/closeInviteModal'
        ),

      setInvitationErrors: (errorsByIndex) =>
        set(
          {
            invitationErrorsByIndex: {
              ...get().invitationErrorsByIndex,
              ...errorsByIndex,
            },
          },
          false,
          'userManagement/setInvitationErrors'
        ),

      setIsCheckingEmail: (isCheckingEmail: boolean) =>
        set({ isCheckingEmail }, false, 'userManagement/setIsCheckingEmail'),

      clearInvitationErrors: () =>
        set(
          { invitationErrorsByIndex: {} },
          false,
          'userManagement/clearInvitationErrors'
        ),

      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    { name: DEVTOOLS_NAME }
  )
)
